import { c } from 'ttag';

import { BadgeCategory } from '@freelancelabs/components';

export type ActionFamily = 'access' | 'role' | 'module' | 'dashboard' | 'mfa' | 'user' | 'partner' | 'admin' | 'other';

const FAMILY_CATEGORY: Record<ActionFamily, BadgeCategory> = {
    access: 'access',
    role: 'role',
    module: 'module',
    dashboard: 'dashboard',
    mfa: 'mfa',
    user: 'user',
    partner: 'partner',
    admin: 'admin',
    other: 'default',
};

// Key order is the menu order; `Object.keys` preserves it, so CURATED_ACTION_CODES and
// the filter options come out already grouped by family.
const CODE_FAMILY: Record<string, ActionFamily> = {
    'organization.portal_opened': 'access',
    'organization.portal_closed': 'access',

    'membership.created': 'role',
    'membership.activated': 'role',
    'membership.role_changed': 'role',
    'membership.revoked': 'role',
    'membership.reinstated': 'role',
    'membership.markets_updated': 'role',

    'organization.module_enabled': 'module',
    'organization.module_disabled': 'module',

    'dashboard.declared': 'dashboard',
    'dashboard.updated': 'dashboard',
    'dashboard.archived': 'dashboard',
    'dashboard.deleted': 'dashboard',
    'dashboard.granted': 'dashboard',
    'dashboard.revoked': 'dashboard',

    'account.mfa_required': 'mfa',
    'account.mfa_not_required': 'mfa',

    'invitation.created': 'user',
    'invitation.sent': 'user',
    'invitation.resent': 'user',
    'invitation.accepted': 'user',
    'contact.onboarded': 'user',
    'user.deleted': 'user',
    'user.partner_list_changed': 'user',

    'partner_list.declared': 'partner',
    'partner_list.updated': 'partner',
    'partner_list.display_changed': 'partner',
    'partner_list.partners_added': 'partner',
    'partner_list.partner_removed': 'partner',
    'partner_list.archived': 'partner',

    'collaborator.created': 'admin',
    'collaborator.bootstrapped': 'admin',
    'collaborator.portfolio_updated': 'admin',
    'need_submission.created': 'admin',
    'role.created': 'admin',
};

// Curated codes sent in `actions` when no type is checked, which bounds the event
// counter (an empty `actions` means "all" to the API).
export const CURATED_ACTION_CODES = Object.keys(CODE_FAMILY);

export const familyOf = (code: string): ActionFamily => CODE_FAMILY[code] ?? 'other';

export const badgeCategoryOf = (code: string): BadgeCategory => FAMILY_CATEGORY[familyOf(code)];

// Resolve labels at call time, not module load: the i18n catalogue may not be loaded at
// import. An unknown code falls back to its raw code.
export const actionLabel = (code: string): string => {
    switch (code) {
        case 'organization.portal_opened':
            return c('Activity log action').t`Portal access opened`;
        case 'organization.portal_closed':
            return c('Activity log action').t`Portal access closed`;
        case 'membership.created':
            return c('Activity log action').t`Membership created`;
        case 'membership.activated':
            return c('Activity log action').t`Membership activated`;
        case 'membership.role_changed':
            return c('Activity log action').t`Role changed`;
        case 'membership.revoked':
            return c('Activity log action').t`Access revoked`;
        case 'membership.reinstated':
            return c('Activity log action').t`Access reinstated`;
        case 'membership.markets_updated':
            return c('Activity log action').t`Scope updated`;
        case 'organization.module_enabled':
            return c('Activity log action').t`Module opened`;
        case 'organization.module_disabled':
            return c('Activity log action').t`Module closed`;
        case 'dashboard.declared':
            return c('Activity log action').t`Dashboard declared`;
        case 'dashboard.updated':
            return c('Activity log action').t`Dashboard updated`;
        case 'dashboard.archived':
            return c('Activity log action').t`Dashboard archived`;
        case 'dashboard.deleted':
            return c('Activity log action').t`Dashboard deleted`;
        case 'dashboard.granted':
            return c('Activity log action').t`Dashboard granted`;
        case 'dashboard.revoked':
            return c('Activity log action').t`Dashboard revoked`;
        case 'account.mfa_required':
            return c('Activity log action').t`Two-factor authentication required`;
        case 'account.mfa_not_required':
            return c('Activity log action').t`Two-factor authentication no longer required`;
        case 'invitation.created':
            return c('Activity log action').t`Invitation created`;
        case 'invitation.sent':
            return c('Activity log action').t`Invitation sent`;
        case 'invitation.resent':
            return c('Activity log action').t`Invitation resent`;
        case 'invitation.accepted':
            return c('Activity log action').t`Invitation accepted`;
        case 'contact.onboarded':
            return c('Activity log action').t`Contact onboarded`;
        case 'user.deleted':
            return c('Activity log action').t`User deleted`;
        case 'user.partner_list_changed':
            return c('Activity log action').t`Partner list changed`;
        case 'partner_list.declared':
            return c('Activity log action').t`Partner list declared`;
        case 'partner_list.updated':
            return c('Activity log action').t`Partner list updated`;
        case 'partner_list.display_changed':
            return c('Activity log action').t`Partner list display changed`;
        case 'partner_list.partners_added':
            return c('Activity log action').t`Partners added`;
        case 'partner_list.partner_removed':
            return c('Activity log action').t`Partner removed`;
        case 'partner_list.archived':
            return c('Activity log action').t`Partner list archived`;
        case 'collaborator.created':
            return c('Activity log action').t`Collaborator created`;
        case 'collaborator.bootstrapped':
            return c('Activity log action').t`Collaborator bootstrapped`;
        case 'collaborator.portfolio_updated':
            return c('Activity log action').t`Portfolio updated`;
        case 'need_submission.created':
            return c('Activity log action').t`Need request created`;
        case 'role.created':
            return c('Activity log action').t`Role created`;
        default:
            return code;
    }
};

export interface ActionDescriptor {
    family: ActionFamily;
    badgeCategory: BadgeCategory;
    label: string;
}

export const describeAction = (code: string): ActionDescriptor => {
    const family = familyOf(code);
    return { family, badgeCategory: FAMILY_CATEGORY[family], label: actionLabel(code) };
};
